import { useCallback, type MutableRefObject, type RefObject } from 'react';

import { updateFingeringLabelContent } from '../utils/updateFingeringLabelContent';
import { swapPdfFingeringData } from '../utils/swapPdfFingeringData';
import { updatePdfFingeringSvg } from '../utils/updatePdfFingeringSvg';
import {
  trackButtonClick,
  trackProjectPageAction,
} from '../services/telemetry';
import { collectSwapGroups } from '../utils/collectSwapGroups';
import type { ScoreSystemData } from './usePdfStateQueue';
import { useFingeringInteractions } from './useFingeringInteractions';

export interface UsePdfFingeringInteractionsParams {
  currentFingeringRef: MutableRefObject<ScoreSystemData[] | null>;
  addToStateQueue: (state: ScoreSystemData[]) => void;
  /** Live-read flag indicating whether the page is currently in
   *  fingering edit mode. We use a ref (not a value) so the pointer
   *  handlers see the latest value without re-binding on every
   *  toggle. */
  isEditModeRef: MutableRefObject<boolean>;
  /** Push a pre-change snapshot onto the edit-session undo stack.
   *  The implementation in the page no-ops outside of edit mode, so
   *  the hook can call this unconditionally. */
  pushEditUndo: (snapshot: ScoreSystemData[]) => void;
  /**
   * Score stack that owns the overlay SVGs. Listeners are delegated
   * here so a ResizeObserver overlay rebuild cannot drop drag/swap
   * handlers.
   */
  rootRef: RefObject<HTMLElement | null>;
  /**
   * When set, digits that carry `data-note-id` edit through this callback
   * instead of the image/group/fingering indexes. The callback owns the
   * undo snapshot and the save queue.
   */
  onEditMainByNoteId?: (noteId: string, value: string) => void;
  /** `wide` is a short tap (contiguous run). A long press passes false. */
  onSwapByNoteId?: (noteId: string, wide: boolean) => void;
}

/**
 * PDF/image fingering edits on top of the shared pointer gestures.
 * XML pages call `useFingeringInteractions` directly.
 */
export const usePdfFingeringInteractions = ({
  currentFingeringRef,
  addToStateQueue,
  isEditModeRef,
  pushEditUndo,
  rootRef,
  onEditMainByNoteId,
  onSwapByNoteId,
}: UsePdfFingeringInteractionsParams) => {
  const updateFingeringData = useCallback(
    (
      imageIndex: number,
      groupIndex: number,
      fingeringIndex: number,
      newValue: number
    ) => {
      if (!currentFingeringRef.current) return;

      pushEditUndo(structuredClone(currentFingeringRef.current));

      const newFingering = structuredClone(currentFingeringRef.current);
      newFingering[imageIndex].fingeringGroupList[groupIndex].fingeringNumList[
        fingeringIndex
      ].mainFingering = newValue;
      currentFingeringRef.current = newFingering;
      addToStateQueue(newFingering);
    },
    [currentFingeringRef, addToStateQueue, pushEditUndo]
  );

  const onEditMain = useCallback(
    (
      target: {
        noteId?: string;
        imageIndex: number;
        groupIndex: number;
        fingeringIndex: number;
        svgText: SVGTextElement;
      },
      value: string
    ) => {
      if (onEditMainByNoteId && target.noteId) {
        if ((target.svgText.textContent || '') !== value) {
          updateFingeringLabelContent(target.svgText, value);
          onEditMainByNoteId(target.noteId, value);
        }
        return;
      }
      const newFingerValue = parseInt(value, 10);
      if (
        !isNaN(newFingerValue) &&
        currentFingeringRef.current &&
        currentFingeringRef.current[target.imageIndex].fingeringGroupList[
          target.groupIndex
        ].fingeringNumList[target.fingeringIndex].mainFingering !==
          newFingerValue
      ) {
        updateFingeringLabelContent(target.svgText, value);
        updateFingeringData(
          target.imageIndex,
          target.groupIndex,
          target.fingeringIndex,
          newFingerValue
        );
        trackButtonClick('PdfProjectPage', 'Edit Main Fingering');
        trackProjectPageAction('edit_main_fingering', 'pdf');
      }
    },
    [currentFingeringRef, onEditMainByNoteId, updateFingeringData]
  );

  const onSwap = useCallback(
    (
      target: { noteId?: string; imageIndex: number; groupIndex: number },
      wide: boolean
    ) => {
      if (onSwapByNoteId && target.noteId) {
        onSwapByNoteId(target.noteId, wide);
        return;
      }
      const groupsToSwap = wide
        ? collectSwapGroups(
            currentFingeringRef.current,
            target.imageIndex,
            target.groupIndex
          )
        : [{ imageIndex: target.imageIndex, groupIndex: target.groupIndex }];

      if (groupsToSwap.length === 0) return;
      if (!currentFingeringRef.current) return;

      pushEditUndo(structuredClone(currentFingeringRef.current));

      const newFingering = swapPdfFingeringData(
        currentFingeringRef.current,
        groupsToSwap
      );
      currentFingeringRef.current = newFingering;
      updatePdfFingeringSvg(newFingering, groupsToSwap);
      addToStateQueue(newFingering);
      trackButtonClick('PdfProjectPage', 'Swap Alternative Fingering');
      trackProjectPageAction('swap_alternative_fingering', 'pdf');
    },
    [addToStateQueue, currentFingeringRef, onSwapByNoteId, pushEditUndo]
  );

  useFingeringInteractions({
    rootRef,
    isEditModeRef,
    onEditMain,
    onSwap,
  });
};
